/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable arrow-body-style */
/* eslint-disable import/prefer-default-export */
import {
  AnimalSystem,
  AnimalType,
  CompoundFeed,
  CompoundFeedV2,
  Farm,
  FarmBaseline,
  Intervention,
  ReportingType,
  StageType,
} from "../../../graphql/types";
import { CFDatabaseFoundation } from "../models/CompoundFeed";
import { getUIDatabaseFoundation } from "./compound-feed-utils";
import { isShrimp } from "../helpers/animals";
import { footprintURL } from "./footprint-url-utils";
import featureFlags from "./featureFlags";

interface FarmDataset {
  footprintReference: string;
  customerId: string;
  farmId: string;
  farmName: string;
  processId: string;
  processName: string;
  displayName: string;
  datasetType: "b" | "i";
  animalType: AnimalType;
  stageName?: string;
  stageType?: StageType;
  reportingType: ReportingType;
  validFrom?: Date;
  validTo?: Date;
  year?: number;
  baselineReference?: string; // datasetType === "i"
}

interface FeedDataset {
  footprintReference: string;
  customerId: string;
  displayName: string;
  datasetType: "f";
  databaseName?: CFDatabaseFoundation;
  year?: number;
}

export type DatasetOption = FarmDataset | FeedDataset;

export interface FootprintCompareOptions {
  label: string;
  options: DatasetOption[];
}

const mountFarmDatasetItem = (
  customerId: string,
  dataType: "b" | "i",
  productionProcess: AnimalSystem,
  farmName: string,
  sourceData: FarmBaseline | Intervention,
  referenceBaseline?: FarmBaseline,
): FarmDataset => {
  const isBaselineData = (
    data: FarmBaseline | Intervention,
  ): data is FarmBaseline => "reportingType" in data;

  let data: FarmBaseline | undefined;

  if (dataType === "b" && isBaselineData(sourceData)) {
    data = sourceData;
  } else if (dataType === "i" && referenceBaseline) {
    data = referenceBaseline;
  }

  const getReferenceData = (
    data: FarmBaseline | undefined,
  ): Pick<FarmDataset, "reportingType" | "validFrom" | "validTo" | "year"> => {
    // Use default values if data is not available
    // This can happen for interventions without a matching baseline
    return {
      reportingType: data?.reportingType ?? ReportingType.FullLca,
      validFrom: data?.validFrom ?? undefined,
      validTo: data?.validTo ?? undefined,
      year:
        data?.year ||
        (data?.validFrom ? new Date(data.validFrom).getFullYear() : undefined),
    };
  };

  const dataset: FarmDataset = {
    footprintReference: sourceData.reference,
    displayName: sourceData.name,
    customerId,
    farmId: productionProcess.farmId,
    farmName,
    processId: productionProcess.id,
    processName: productionProcess.name,
    datasetType: dataType,
    animalType: productionProcess.animalType,
    ...getReferenceData(data),
  };

  const source = featureFlags.ffBugsQ3s1 && data ? data : sourceData;

  if (isShrimp(productionProcess.animalType) && source.stages) {
    const stageType = source.stages[0]?.type;
    if (stageType) {
      dataset.stageType = stageType;
    }
  }

  if (dataType === "i" && !isBaselineData(sourceData) && referenceBaseline) {
    dataset.baselineReference = sourceData.baseline;
  }

  return dataset;
};

const getProductionProcessItems = (
  customerId: string,
  farmName: string,
  animalSystems: AnimalSystem[],
) => {
  const farmData: DatasetOption[] = [];

  animalSystems.forEach((productionProcess) => {
    if (!productionProcess) {
      return;
    }

    const baselines = productionProcess.baselines?.items;
    const interventions = productionProcess.interventions?.items;

    const mountedBaselines: DatasetOption[] = [];

    if (baselines) {
      baselines.forEach((baseline) => {
        if (!baseline) {
          return;
        }

        const item = mountFarmDatasetItem(
          customerId,
          "b",
          productionProcess,
          farmName,
          baseline,
        );

        mountedBaselines.push(item);
      });

      farmData.push(...mountedBaselines);
    }

    const mountedInterventions: DatasetOption[] = [];

    if (interventions) {
      interventions.forEach((intervention) => {
        if (!intervention) {
          return;
        }

        const referenceBaseline =
          baselines?.find(
            (baseline) => baseline?.reference === intervention.baseline,
          ) ?? undefined;

        const item = mountFarmDatasetItem(
          customerId,
          "i",
          productionProcess,
          farmName,
          intervention,
          referenceBaseline,
        );

        mountedInterventions.push(item);
      });
    }

    farmData.push(...mountedInterventions);
  });

  return farmData;
};

export const formatFarmsData = (customerId: string, data: Farm[]) => {
  const formattedData: FootprintCompareOptions[] = [];

  data.forEach((farm) => {
    if (!farm) {
      return;
    }

    const dataset: FootprintCompareOptions = {
      label: farm.name,
      options: getProductionProcessItems(
        customerId,
        farm?.name,
        farm?.animalSystems?.items as AnimalSystem[],
      ),
    };

    formattedData.push(dataset);
  });

  return formattedData;
};

export const formatFeedsData = (
  customerId: string,
  feedsV1: CompoundFeed[],
  feedsV2: CompoundFeedV2[],
): FootprintCompareOptions[] => {
  const mergedFeeds = [
    ...feedsV1.map((feed) => ({
      id: feed.id,
      name: feed.name,
      databaseFoundation: undefined,
      year: feed.year.value,
    })),
    ...feedsV2.map((feed) => ({
      id: feed.id,
      name: feed.name,
      databaseFoundation: feed.databaseFoundation ?? undefined,
      year: feed.year,
    })),
  ].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const formattedData: FeedDataset[] = mergedFeeds.map((feed) => ({
    footprintReference: feed.id,
    displayName: feed.name,
    customerId,
    datasetType: "f" as const,
    databaseName: getUIDatabaseFoundation(feed.databaseFoundation),
    year: feed.year,
  }));

  return [
    {
      label: "Compound feeds",
      options: formattedData,
    },
  ];
};

export function isFarmDataset(dataset: DatasetOption): dataset is FarmDataset {
  return dataset.datasetType === "b" || dataset.datasetType === "i";
}

export function isFeedDataset(dataset: DatasetOption): dataset is FeedDataset {
  return dataset.datasetType === "f";
}

export const buildFootprintURL = (
  baselineOption: DatasetOption,
  comparisonOption?: DatasetOption,
  comparisonSelectedStage?: string,
) => {
  const baselineReference = baselineOption.footprintReference;
  const baselineCustomerID = baselineOption.customerId;
  const baselineType = baselineOption.datasetType;
  const dataSetName = baselineOption.displayName;

  let baselineFarmID = "";
  let farmName: string | undefined;
  let baselineProcessID = "";
  let processName: string | undefined;
  let animalType: AnimalType | undefined;
  let databaseName: CFDatabaseFoundation | undefined;

  if (isFarmDataset(baselineOption)) {
    baselineFarmID = baselineOption.farmId;
    farmName = baselineOption.farmName;
    baselineProcessID = baselineOption.processId;
    processName = baselineOption.processName;
    animalType = baselineOption.animalType;
  }

  if (isFeedDataset(baselineOption)) {
    databaseName = baselineOption.databaseName;
  }

  const variables = {
    farmName,
    processName,
    dataSetName,
    animalType,
    databaseName,
  };

  if (comparisonOption) {
    const comparisonReference = comparisonOption.footprintReference;
    const comparisonCustomerID = comparisonOption.customerId;
    const comparisonType = comparisonOption.datasetType;
    const comparisonName = comparisonOption.displayName;

    let comparisonFarmID: string | undefined;
    let comparisonProcessID: string | undefined;

    if (isFarmDataset(comparisonOption)) {
      comparisonFarmID = comparisonOption.farmId;
      comparisonProcessID = comparisonOption.processId;
    }

    return {
      path: footprintURL({
        baselineCustomerID,
        baselineFarmID,
        baselineProcessID,
        baselineType,
        baselineReference,
        comparisonFarmID,
        comparisonProcessID,
        comparisonType,
        comparisonReference,
        comparisonCustomerID,
        comparisonSelectedStage,
      }),
      variables: {
        ...variables,
        comparisonName,
      },
    };
  }

  return {
    path: footprintURL({
      baselineCustomerID,
      baselineFarmID,
      baselineProcessID,
      baselineType,
      baselineReference,
    }),
    variables,
  };
};
